import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';

/**
 * Triggers validation for all controls and sub-controls of given Abstract Controls.
 * Returns validation state.
 *
 * @param {...AbstractControl[]} controls
 * @returns {boolean}
 */
export const getValidationState = (...controls: AbstractControl[]): boolean => {
    return controls.reduce((valid: boolean, control: AbstractControl) => {
        const nextValid = validateControl(control) == null;
        return valid && nextValid;
    }, true);
};

/**
 * Triggers validation for all controls and sub-controls of given Abstract Controls.
 * Returns validation errors.
 *
 * @param {...AbstractControl[]} controls
 * @returns {boolean}
 */
export const getValidationErrors = (...controls: AbstractControl[]): ValidationErrors | null => {
    const controlErrors = controls.map(control => validateControl(control));
    return mergeErrors(controlErrors);
};

/**
 * Recusively update value and validity of all controls in the form
 *
 * @param {AbstractControl} control
 */
export const updateValueAndValidity = (control: AbstractControl, opts?: { emitEvent?: boolean; }): void => {
    if (control instanceof UntypedFormGroup) {
        const group = (control as UntypedFormGroup);
        for (const field in group.controls) {
            const c = group.controls[field];
            updateValueAndValidity(c, opts);
        }
    }
    else if (control instanceof UntypedFormArray) {
        const group = (control as UntypedFormArray);
        for (const field in group.controls) {
            const c = group.controls[field];

            updateValueAndValidity(c, opts);
        }
    }

    control.updateValueAndValidity(opts);
};


/**
 * Triggers validation for all controls and subforms of given FormGroup.
 * Returns validation state.
 *
 * @param {AbstractControl} control
 * @returns {boolean}
 */
const validateControl = (control: AbstractControl): ValidationErrors | null => {
    if (control.valid) return null;
    if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
        return control.errors;
    } else if (control instanceof UntypedFormGroup) {
        return validateFormGroup(control);
    } else if (control instanceof UntypedFormArray) {
        return validateFormArray(control);
    }
    throw Error("Can't validate");
};


/**
 * Triggers validation for all controls and subforms of given FormGroup.
 * Returns validation state.
 *
 * @param {FormGroup} formGroup
 * @returns {boolean}
 */
const validateFormGroup = (formGroup: UntypedFormGroup): ValidationErrors | null => {
    if (formGroup.valid) {
        return null;
    }
    const controlErrors = Object
        .values(formGroup.controls)
        .map(control => validateControl(control));
    return mergeErrors([formGroup.errors, ...controlErrors]);
};

/**
 * Triggers validation for all controls given FormArray.
 * Returns validation state.
 *
 * @param {FormArray} formArray
 * @returns {boolean}
 */

const validateFormArray = (formArray: UntypedFormArray): ValidationErrors | null => {
    if (formArray.valid) {
        return null;
    }
    const controlErrors = formArray
        .controls
        .map(control => validateControl(control));
    return mergeErrors([formArray.errors, ...controlErrors]);
};


/**
 * Merge errors from different validators
 *
 * @param {((ValidationErrors | null)[])} arrayOfErrors
 * @return {*}  {(ValidationErrors | null)}
 */
const mergeErrors = (arrayOfErrors: (ValidationErrors | null)[]): ValidationErrors | null => {
    let res: ValidationErrors = {};
    arrayOfErrors.forEach((errors: ValidationErrors | null) => {
        res = errors != null ? { ...res, ...errors } : res;
    });

    return Object.keys(res).length === 0 ? null : res;
};